import { useContext, useEffect, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import LoadingSpinner from '../../../library/SimpleControls/LoadingSpinner';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import InquiryReportService from './InquiryReportService';
import StatsBar from './components/StatsBar';
import FiltersBar from './components/FiltersBar';
import ResultsTable from './components/ResultsTable';
import InquiryDetailModal from './components/InquiryDetailModal';

const InquiryManagement = () => {
    const { globalState } = useContext(GlobalContext);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState<any>(null);

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const domain = useMemo(() => addUserSpecificDomains(new Map([...globalState.domain]), data), [globalState.domain, data]);

    useEffect(() => {
        dispatch({
            type: 'DATA_INIT',
            payload: { formConfig, data, domain, internal, routeInfo },
        });
    }, []);

    const refreshInquiries = async () => {
        const criteria = state.data?.searchCriteria || InquiryReportService.getDefaultSearchCriteria();
        const [response, stats] = await Promise.all([
            InquiryReportService.search(criteria),
            InquiryReportService.getStats(criteria),
        ]);

        const payload = response?.data ?? {};
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'inquiryList', value: payload?.data ?? [], errorMessages: [] } });
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: {
                dataKey: 'pagination',
                value: {
                    total: payload?.total ?? 0,
                    page: criteria.page,
                    limit: criteria.limit,
                },
                errorMessages: [],
            },
        });
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'stats', value: stats, errorMessages: [] } });
    };

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            await InquiryReportService.exportCSV(state.data?.searchCriteria || InquiryReportService.getDefaultSearchCriteria());
            toast.success('Inquiry report exported successfully');
        } catch (error: any) {
            console.error('Inquiry export error:', error);
            const message = error?.response?.data?.message || error?.message;
            toast.error(message || 'Failed to export inquiry report.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="main flex-1">
                {!state?.data && <LoadingSpinner />}
                {state?.data && (
                    <div className="bg-white py-5">
                        <div className="max-1140 mx-auto">
                            <div className="row g-3 align-items-start mb-4">
                                <div className="col">
                                    <h2 className="font-26 font-600 mb-1">{state.formConfig?.title || 'Inquiry Management'}</h2>
                                    <p className="font-16 text-muted mb-0">{state.formConfig?.subTitle || (state.formConfig as any)?.subtitle || 'Manage all inquiries received.'}</p>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-skyblue w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3"
                                        onClick={handleExport}
                                        disabled={isExporting}>
                                        {isExporting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-box-arrow-up-right font-16 me-1"></i>
                                                <span>Export</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <StatsBar />
                            <FiltersBar />
                            <ResultsTable onViewInquiry={setSelectedInquiry} />

                            <InquiryDetailModal
                                inquiry={selectedInquiry}
                                show={!!selectedInquiry}
                                onSaved={refreshInquiries}
                                onHide={() => setSelectedInquiry(null)}
                            />
                        </div>
                    </div>
                )}
                <DebugBarControl />
            </div>
        </SmartContext.Provider>
    );
};

export default InquiryManagement;